/* ==========================================================================
   DeClaire & Friends - section scroll animations
   --------------------------------------------------------------------------
   Adds a fade-and-rise to each section as it scrolls into view, and staggers
   repeated children (product cards, columns, grid tiles) inside it.

   Deliberate choices, because each one is a bug waiting to happen:

   * Anything already inside the viewport on load is never hidden. Hiding it
     after first paint would produce a visible flash on the hero.
   * The reveal transform is removed once the transition ends, otherwise the
     section becomes a containing block and every position: sticky element
     inside it (product info column, collection filters) silently stops
     sticking.
   * Header, drawers, popups and any sticky or fixed section are skipped.
   * Horizontal carousel tracks are never staggered, as translating their
     children fights scroll-snap.
   * Re-runs cleanly on shopify:section:load so the theme editor behaves.
   ========================================================================== */

(function () {
	'use strict'

	if (!('IntersectionObserver' in window)) return
	if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return

	var MARK = 'data-dcf-reveal'
	var PROCESSED = 'dcfAnimProcessed'
	var MAX_STAGGER = 10

	/* Sections that must never be transformed. */
	var SKIP_SECTION = [
		'.shopify-section--header',
		'.shopify-section--announcement-bar',
		'.shopify-section--announcement-bar-custom',
		'.shopify-section--popup',
		'.shopify-section--privacy-banner',
	].join(',')

	/* Custom elements that mark a section as overlay / chrome rather than content. */
	var SKIP_CONTAINS =
		'site-header, cart-drawer, search-drawer, navigation-drawer, ' +
		'newsletter-popup, privacy-bar, quick-buy-drawer, cart-notification-drawer'

	/* Containers whose direct children make a good stagger group. */
	var GROUP_SELECTORS = [
		'[class*="__grid"]',
		'[class*="__items"]',
		'[class*="__cards"]',
		'[class*="__list"]',
		'[class*="__columns"]',
		'.product-list',
	].join(',')

	var observer = null

	function isHidden(el) {
		return el.offsetWidth === 0 && el.offsetHeight === 0
	}

	function isStuck(el) {
		var pos = getComputedStyle(el).position
		return pos === 'sticky' || pos === 'fixed'
	}

	/* Translating a child of a horizontally scrolling track interferes with
	   scroll-snap, so those groups are left alone. */
	function inScroller(el) {
		var node = el
		for (var i = 0; node && i < 6; i++) {
			var overflow = getComputedStyle(node).overflowX
			if (overflow === 'auto' || overflow === 'scroll') return true
			node = node.parentElement
		}
		return false
	}

	function sectionIsEligible(section) {
		if (section.dataset[PROCESSED]) return false
		if (section.matches(SKIP_SECTION)) return false
		if (section.querySelector(SKIP_CONTAINS)) return false
		if (isStuck(section)) return false
		if (section.querySelector('[data-dcf-no-anim]')) return false
		if (!section.getBoundingClientRect().height) return false
		return true
	}

	/* Picks the first container inside the section holding three or more
	   siblings worth staggering. */
	function findGroup(section) {
		var candidates = section.querySelectorAll(GROUP_SELECTORS)

		for (var i = 0; i < candidates.length; i++) {
			var container = candidates[i]
			var children = Array.prototype.filter.call(
				container.children,
				function (child) {
					return (
						child.nodeType === 1 &&
						!isHidden(child) &&
						!isStuck(child)
					)
				},
			)

			if (children.length < 3) continue
			if (inScroller(container)) continue

			return children
		}

		return null
	}

	function prime(el, kind, delay) {
		/* Only elements that start below the fold are hidden. Anything already
		   painted, or scrolled past on a restored scroll position, is left
		   exactly as the browser rendered it. */
		var rect = el.getBoundingClientRect()
		var viewport = window.innerHeight || document.documentElement.clientHeight
		if (rect.top < viewport) return false

		el.setAttribute(MARK, kind)
		if (delay) el.style.setProperty('--dcf-reveal-delay', delay + 'ms')
		return true
	}

	function release(el) {
		el.classList.add('dcf-in')

		var settle = function () {
			el.classList.add('dcf-done')
			el.style.removeProperty('--dcf-reveal-delay')
			el.removeEventListener('transitionend', settle)
		}

		el.addEventListener('transitionend', settle)
		/* transitionend does not fire if the element is scrolled past while
		   still off-screen, so guarantee the cleanup. */
		setTimeout(settle, 1600)
	}

	function getObserver() {
		if (observer) return observer

		observer = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (entry) {
					if (!entry.isIntersecting) return
					release(entry.target)
					observer.unobserve(entry.target)
				})
			},
			{ rootMargin: '0px 0px -8% 0px', threshold: 0.01 },
		)

		return observer
	}

	function setupSection(section) {
		if (!sectionIsEligible(section)) return
		section.dataset[PROCESSED] = '1'

		var io = getObserver()
		var group = findGroup(section)

		if (group) {
			/* Stagger the repeated children and let the section itself sit
			   still, so the two animations do not compound. */
			group.forEach(function (child, index) {
				var delay = Math.min(index, MAX_STAGGER) * 70
				if (prime(child, 'up', delay)) io.observe(child)
			})

			var heading = section.querySelector(
				'[class*="section-header"], [class*="__header"], [class*="__heading"]',
			)
			if (
				heading &&
				group.indexOf(heading) === -1 &&
				prime(heading, 'up', 0)
			) {
				io.observe(heading)
			}
			return
		}

		if (prime(section, 'up', 0)) io.observe(section)
	}

	function run(root) {
		var scope = root || document
		var sections = scope.querySelectorAll('.shopify-section')
		Array.prototype.forEach.call(sections, setupSection)
	}

	function boot() {
		run(document)
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot, { once: true })
	} else {
		boot()
	}

	/* Theme editor: a re-rendered section arrives with no reveal state. */
	document.addEventListener('shopify:section:load', function (event) {
		var section = event.target
		if (!section) return
		delete section.dataset[PROCESSED]
		setupSection(section)
	})
})()
